import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

type Status = 'idle'

// One id for every autosave, so a burst of saves refreshes a single toast instead of stacking.
const TOAST_ID = 'autosave'

const typing = () => {
  const el = document.activeElement as HTMLElement | null
  return !!el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
}

let deferred = false
// Saves land mid-typing (debounced); confirm once the field is left, not on every pause.
function showSaved(text: string) {
  if (!typing()) return void toast.success(text, { id: TOAST_ID, duration: 1500 })
  if (deferred) return
  deferred = true
  document.addEventListener(
    'focusout',
    () => {
      deferred = false
      toast.success(text, { id: TOAST_ID, duration: 1500 })
    },
    { once: true },
  )
}

/** Tracks an autosave: `track(promise)` shows a short "Saved" toast (after the field is left), or "Couldn't save" at once.
 *  Rejections are swallowed here, so callers can drop their own `.catch`. */
export function useSavedHint() {
  const { t } = useTranslation()
  const track = useCallback(
    (save: Promise<unknown> | void): Promise<boolean> =>
      Promise.resolve(save).then(
        () => {
          showSaved(t('common.savedHint'))
          return true
        },
        () => {
          toast.error(t('common.saveHintFailed'), { id: TOAST_ID })
          return false
        },
      ),
    [t],
  )
  return { status: 'idle' as Status, track }
}

/** Kept so call sites stay unchanged; the feedback is the toast from `track`. */
export function SavedHint(_: { status: Status; className?: string }) {
  return null
}
